import { EventEmitter, Injectable } from "@angular/core";
import { SupportTicket, USER_ROLE, User } from "../common/user";
import { OrganisationService } from "../organisation/organisation.service";
import { LinqsetRepositoryService } from "./linqset-repository.service";
import { CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, INSIGHTS_FIELD, isRoleSystem, Linqset, LinqsetNode, MODEL_TYPE, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY, PEOPLE_SYSTEMS_LAYOUTS, PEOPLE_SYSTEMS_VIEWS } from "../common/linqset-model";
import { GraphProcessingService } from "./graph-processing.service";
import { LabelService } from "./label.service";
import { ModelService } from "../organisation/models/model-service";
import { EmissionFactor, MODEL_EXPIRY_DAYS, ModelDetails, Organisation, Project, TagGroup, createModelInsights, createModelTags } from "../common/organisation";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../ui-components/dialogs/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { GroupService } from "./group.service";
import { TagsService } from "../organisation/tags/tags-service";
import { UserDialogComponent, UserDialogData } from "../user/user-dialog.component";
import { UserService } from "../user/user.service";
import { Router } from "@angular/router";
import { firstValueFrom, lastValueFrom } from "rxjs";
import { emptyNodes } from "./empty-nodes";
import { FilterService } from "./filter.service";
import { CREATE_MODEL_WITH_AI_URL, DEFAULT_URL, PROJECTS_URL, SKETCH_URL } from "../app-urls";
import { FilterComponent } from "../model-components/filter/filter.component";
import { RestoreModelDialogComponent } from "../organisation/models/restore-model-dialog.component";
import { DownloadService, EXPORT_TYPE } from "./download.service";
import { TicketDialogComponent, clearTicketDraft } from "../user/support/ticket-dialog.component";
import { v4 } from "uuid";
import { MatSnackBar } from "@angular/material/snack-bar";
import { EmissionFactorDialogComponent } from "../organisation/carbon/emission-factor-dialog.component";
import { NodeService } from "./node.service";
import { ERROR_CODE, getUserMessage } from "../common/exceptions";
import { HelpService } from "../help/help-services";
import { helpUrlFrom } from '../help/guides/help-guides.service';
import { LinqYFilesExportService } from "../canvas/yfiles-canvas-export.service";
import { GraphService } from "../canvas/graph.service";
import { BackupService } from "../organisation/backups/backup.service";
import { AdjacencyTypes, Command, GraphComponent, IGraph, INode } from "@yfiles/yfiles";
import { PdfService } from "./pdf.service";

export { FUNCTION, FUNCTION_LEVEL } from './functions'
import { FUNCTION, FUNCTION_LEVEL } from './functions'


export interface Function {
    _key:FUNCTION,
    icon:string,
    name:string,
    description:string,
    role:USER_ROLE,
    level:FUNCTION_LEVEL,
    disabled?:boolean
}

// the parts of the graph component a function acts on; the component matches this, so the service need not import it
export interface GraphComponentHost {
    graphComponent:GraphComponent
    clearingGraph:boolean
    newLinqset():Promise<void>
    getSelectedNodes():INode[]
    toggleMagnifyingGlass():void
    toggleGroups(expand:boolean):void
}

// the choices when creating a model
const NEW_MODEL_AI = "Use AI"
const NEW_MODEL_BLANK = "Start blank"

@Injectable({
    providedIn: 'root'
})
export class FunctionService {
    functionalist:Function[]

    linqsetLoadedEvent = new EventEmitter<string>()
    reloadTicketsEvent = new EventEmitter<string>()

    constructor(private organisationService:OrganisationService, private dialog: MatDialog, private linqsetRepository:LinqsetRepositoryService,
        private graphProcessingService:GraphProcessingService, private labelService:LabelService, private modelService:ModelService, private nodeService:NodeService, private backupService:BackupService,
        private filterService:FilterService, private groupService:GroupService, private tagsService:TagsService, private snackBar:MatSnackBar, private helpService:HelpService, private pdfService:PdfService,
        private userService:UserService, private router: Router, private downloadService:DownloadService, private exportService:LinqYFilesExportService, private graphService:GraphService) {
        this.functionalist = this.getFullFunctionList()
        // a restored backup replaces the model, so reload it
        this.backupService.backupRestoredEvent.subscribe(() => this.loadLinqsets())
    }

    // the functions, each marked disabled if the user's role in the organisation, project or model doesn't allow it.
    // Each call gets its own copies: the toolbar, menu and search ask about different models, and shouldn't change
    // each other's answers
    getFunctionMap(excludeDisabled?:boolean, linqsetParam?:Linqset, projectParam?:Project) {
        const organisation = this.organisationService.organisation
        const linqset = linqsetParam || this.linqsetRepository.linqset
        const project = projectParam || (organisation && linqset && organisation.projects?.find(p => p._key === linqset.workspaceKey))

        return this.functionalist.reduce((m, f) => {
            let disabled = false
            if (f.level === FUNCTION_LEVEL.ORGANISATION)
                disabled = !organisation || organisation.role > f.role
            if (f.level === FUNCTION_LEVEL.PROJECT)
                disabled = !project || project.role > f.role
            if (f.level === FUNCTION_LEVEL.MODEL || f.level === FUNCTION_LEVEL.NODE)
                disabled = !linqset?.model || linqset.model.role > f.role

            if (!excludeDisabled || !disabled)
                m[f._key] = {...f, disabled}
            return m
        }, {})
    }

    private getFullFunctionList():Function[] {
        return [
            {_key:FUNCTION.CREATE_PROJECT, icon:"medical_services", name:"Create project", description:"Create a new project for this organisation", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.DELETE_PROJECT, icon:"delete", name:"Delete project", description:"Delete the current project", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.PROJECT},
            {_key:FUNCTION.CREATE_MODEL, icon:"add_circle_outline", name:"Create model", description:"Create a new model for this organisation", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.EDIT_MODEL_TAGS, icon:"new_label", name:"Model tags", description:"Edit the tags that can be applied to models", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.CREATE_TAG_GROUP, icon:"new_label", name:"Create node tag group", description:"Create a new node tag group for this organisation", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.CREATE_ORGANISATION, icon:"create", name:"Create new organisation", description:"Create a new organisation", role:USER_ROLE.READ, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.EDIT_USER_DETAILS, icon:"settings", name:"My user settings", description:"Edit your user settings", role:USER_ROLE.READ, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.EDIT_PROJECT, icon:"edit", name:"Edit project details", description:"Edit the current project", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.PROJECT},
            {_key:FUNCTION.EDIT_ORGANISATION, icon:"edit", name:"Edit organisation details", description:"Edit the current organisation", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.MANAGE_USERS, icon:"people", name:"Manage users", description:"Manage the users associated with this organisation", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.CANCEL_CHANGES, icon:'cancel', name:"Cancel changes", description:"Cancel unsaved changes to the model", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.DELETE_MODEL, icon:"delete", name:"Delete model", description:"Delete the current model", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.EDIT_MODEL_DETAILS, icon:"edit", name:"Edit model details", description:"Edit the details for this model", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.SAVE_MODEL, icon:"save", name:"Save model (Ctrl+S)", description:"Save the current model changes. Ctrl+S (⌘S on Mac) also saves the model.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.REPLACE_CURRENT_STATE, icon:"published_with_changes", name:"Replace current state", description:"Replace current state model with project model", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.DOWNLOAD_MODEL, icon:"download", name:"Download model", description:"Download the model as a JSon file", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.ZOOM_IN, icon:"zoom_in", name:"Zoom in", description:"Zoom in one level", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.ZOOM_OUT, icon:"zoom_out", name:"Zoom out", description:"Zoom out one level", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.FIT_TO_SCREEN, icon:"fullscreen", name:"Fit to screen (F)", description:"Fit model to screen. Press F on the canvas to do the same.", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.RETURN_TO_ORIGINAL, icon:"fullscreen_exit", name:"Return to original size", description:"", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.CUT, icon:"content_cut", name:"Cut", description:"Cut currently selected model items", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.COPY, icon:"content_copy", name:"Copy", description:"Copy currently selected model items", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.PASTE, icon:"content_paste", name:"Paste", description:"Paste cut/copied model items", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.UNDO, icon:"undo", name:"Undo", description:"Undo last model change", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.REDO, icon:"redo", name:"Redo", description:"Redo undone model change", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.COLLAPSE_ALL, icon:"remove_from_queue", name:"Collapse all groups", description:"Expand all groups in model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.EXPAND_ALL, icon:"add_to_queue", name:"Expand all groups", description:"Collapse all groups in model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.REMOVE_GROUPS, icon:"work_off", name:"Remove all groups", description:"Remove all groups in model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.CREATE_GROUPS, icon:"work_on", name:"Create groups", description:"Group model by based on criteria", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.RESET_LAYOUT, icon:"device_hub", name:"Reset layout", description:"Reset layout of the model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.TOGGLE_VIEW_DIRECTION, icon:"swap_vert", name:"Toggle view direction", description:"Toggle direction, top down or bottom up", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.MANAGE_LABELS, icon:"label", name:"Show labels", description:"Set different labels in the nodes", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.RECALCULATE_INSIGHTS, icon:"refresh", name:"Recalculate insights", description:"Run model insights processing", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.APPLY_FILTER, icon:"filter_alt", name:"Create filter", description:"Open filter dialog to select filter options", role:USER_ROLE.READ, level:FUNCTION_LEVEL.NODE},
            {_key:FUNCTION.CLEAR_FILTER, icon:"filter_alt_off", name:"Clear filter", description:"Clear filtered nodes", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.TOGGLE_INSIGHTS, icon:"right_panel_open", name:"Toggle insights", description:"Open or close insights pane", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.TOGGLE_LEFT_PANE, icon:"left_panel_open", name:"Toggle left pane", description:"Open or close left pane", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.SELECT_TAG, icon:"sell", name:"Apply tag", description:"Apply tag to selected action nodes", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.REMOVE_TAGS, icon:"delete", name:"Remove tags", description:"Remove tags from selected action nodes", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.GROUP_SELECTION, icon:"ad_group", name:"Group selection", description:"Create a group around the selected node items", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.UNGROUP_SELECTION, icon:"ad_group_off", name:"Ungroup selection", description:"Remove selected groups", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.PAY_SUBSCRIPTION, icon:"subscriptions", name:"Request extension", description:"Request an extension to the organisation account", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.LOGOUT, icon:"exit_to_app", name:"Logout", description:"Log user out of Linq", role:USER_ROLE.READ, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.BACKUP_MODEL, icon:"backup", name:"Create model backup", description:"Takes a backup of the model edges and nodes.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.RESTORE_BACKUP, icon:"settings_backup_restore", name:"Restore latest model backup", description:"Restores latest backup of the model.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.RESTORE_MODEL, icon:"restore_from_trash", name:"Manage archived models", description:"Delete or restore a previously archived model.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.MERGE_NODES, icon:"merge", name:"Merge nodes into single node", description:"Merge a set of system/role nodes into a single node.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.REQUEST_ACCESS, icon:"key_vertical", name:"Request access to a resource", description:"Request access to a model or project", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.SWITCH_TO_PAN_MODE, icon:"pan_tool", name:"Switch to pan mode (ALT + drag)", description:"Pan graph (ALT + drag)", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.SWITCH_TO_SELECT_MODE, icon:"arrow_selector_tool", name:"Switch to select mode (Esc)", description:"Select mode", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.CREATE_TICKET, icon:"add_circle", name:"Create support ticket", description:"Create a new suport ticket and send to Linq", role:USER_ROLE.READ, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.MOVE_SELECTION_TO_MODEL, icon:"move_selection_down", name:"Move downstream of selected actions to a new model", description:"Move all nodes downstream of selected action nodes to a new model.", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.MAINTAIN_EMISSION_FACTOR, icon:"co2", name:"Add emissions factor", description:"Add a new emissions factor to this organisation", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.DELETE_EMISSION_FACTOR, icon:"co2", name:"Delete emissions factor", description:"Delete emissions factor from this organisation", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.CHANGE_MODEL_TYPE, icon:"graph_2", name:"Change model view filter", description:"Provides a filtered view of the nodes in the model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.SHOW_HELP_GUIDE, icon:"menu_book", name:"Show help guides", description:"Open the help guides, at a guide if one is given", role:USER_ROLE.READ, level:FUNCTION_LEVEL.SYSTEM},
            {_key:FUNCTION.REFRESH_INSIGHTS, icon:"refresh", name:"Refresh insights", description:"Refresh all insights for organisation", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.OPEN_MODELLING_AI, icon:"wand_stars", name:"Build model with AI", description:"Tell AI about the process and it builds this empty model: the steps, the people and systems", role:USER_ROLE.WRITE, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.CLOSE_ORGANISATION, icon:"cancel", name:"Close organisation account", description:"Close down organisation account at end of month.", role:USER_ROLE.ADMIN, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.TOGGLE_TEMPORAL_MODEL, icon:"time_auto", name:"Open/close temporal model", description:"Run the model with different loads and capacities", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.COMPARE_WITH_CURRENT_STATE, icon:"compare", name:"Compare with current state", description:"Show the model in this project over the same model in current state: what the project removed is shown in grey", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.SCREEN_TOUR, icon:"tour", name:"Explain screen to me", description:"Explains each component of the screen.", role:USER_ROLE.READ, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.TOGGLE_MAGNIFYING_GLASS, icon:"feature_search", name:"Toggle magnifying glass", description:"Turn on/off magnifying glass to zoom on nodes.", role:USER_ROLE.READ, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.TOGGLE_HEATMAP, icon:"key_visualizer", name:"Toggle static heatmap for selected attribute", description:"Turn on/off static heatmap that highlights based on selected label attribute..", role:USER_ROLE.READ, level:FUNCTION_LEVEL.ORGANISATION},
            {_key:FUNCTION.CHANGE_LAYOUT, icon:"modeling", name:"Change layout of model", description:"Select a different layout to view the current model", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL},
            {_key:FUNCTION.INCREMENTAL_LAYOUT, icon:"tactic", name:"Incremental layout", description:"Lays out changed nodes keeping rest of graph", role:USER_ROLE.READ, level:FUNCTION_LEVEL.MODEL}

        ]
    }

    public async executeFunction(fn:FUNCTION, value?:any, linqsetParam?:Linqset, projectParam?:Project, graphComponentParam?:GraphComponent, gcComponent?:GraphComponentHost, tagGroup?:TagGroup) {
        const organisation = this.organisationService.organisation
        const linqset = linqsetParam || this.linqsetRepository.linqset
        const project = projectParam || (linqset && linqset.isProjectModel() && (organisation && organisation.projects?.find(p => p._key === linqset.workspaceKey)))
        const graphComponent:GraphComponent = graphComponentParam || gcComponent?.graphComponent
        const user = this.userService.getUser()

        const map = this.getFunctionMap(true, linqset, project)
        if (!map[fn] || map[fn].disabled) return

        switch(fn) {
            case FUNCTION.MANAGE_USERS:
                this.router.navigateByUrl(`organisations/${organisation._key}/users`)
                break
            case FUNCTION.CREATE_ORGANISATION:
                const dialogRef1 = this.organisationService.openOrganisationDialog()

                dialogRef1.afterClosed().subscribe(async result => {
                    if (result) {
                        await this.organisationService.saveOrganisation(result, true)
                    }
                })
                break

            case FUNCTION.EDIT_ORGANISATION:
                this.editOrganisation(organisation)

                break
            case FUNCTION.EDIT_MODEL_TAGS:
                if (!organisation.modelTags || !organisation.modelTags._key) {
                    organisation.modelTags = createModelTags()
                }
                this.tagsService.openTagGroupDialog(organisation.modelTags)

                break
            case FUNCTION.EDIT_USER_DETAILS:
                const dialogRef = this.dialog.open(UserDialogComponent, {data:{user, isInvite:false} as UserDialogData, width:'520px', maxWidth:'95vw'});

                // the dialog returns a copy, so the user only changes once the save works. The canvases showing
                // models apply a change to how they are drawn themselves (see UserService.preferencesChangedEvent)
                dialogRef.afterClosed().subscribe(async (result:User|undefined) => {
                    if (result) await this.userService.saveUser(result)
                })
                break
            case FUNCTION.CREATE_PROJECT:
                this.createProject(organisation)
                break
            case FUNCTION.CREATE_MODEL:
                this.createModel(organisation, project)
                break
            case FUNCTION.CREATE_TAG_GROUP:
                this.tagsService.openTagGroupDialog(value)

                break
            case FUNCTION.DOWNLOAD_MODEL:
                const {organisationLinqset} = this.linqsetRepository
                switch (value) {
                    case EXPORT_TYPE.CSV:
                        this.downloadService.createCSVDownload(organisation, linqset)
                        break
                    case EXPORT_TYPE.SYSTEM_HOURS_CSV:
                        if (organisationLinqset)
                            this.downloadService.hoursByModelCSVDownload(organisationLinqset, NODE_SUPERTYPE.SYSTEM)
                        break
                    case EXPORT_TYPE.ROLE_HOURS_CSV:
                        if (organisationLinqset)
                            this.downloadService.hoursByModelCSVDownload(organisationLinqset, NODE_SUPERTYPE.ROLE)

                        break
                    case EXPORT_TYPE.JSON:
                        this.downloadService.createJsonDownload(organisation, linqset)
                        break
                    // the picture exports draw the canvas being shown
                    case EXPORT_TYPE.PDF:
                        if (!graphComponent) break
                        const pdf = await this.pdfService.generateGraph(graphComponent.graph)
                        pdf.save(linqset.name + '-model.pdf');

                        break
                    case EXPORT_TYPE.PNG:
                        if (graphComponent) this.exportService.exportPNG(graphComponent.graph, linqset.name)
                        break
                    case EXPORT_TYPE.JPG:
                        if (graphComponent) this.exportService.exportJPG(graphComponent.graph, linqset.name)
                        break
                }
                break
            case FUNCTION.MANAGE_LABELS:
                this.graphService.removeHeatmap(graphComponent)
                this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, value||INSIGHTS_FIELD.NONE)
                break
            case FUNCTION.SELECT_TAG:
                const selection = graphComponent?.selection.filter(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.ACTION)
                if (value && selection && tagGroup) {
                    selection.forEach(n => {
                        if (!n.tag.node.properties) n.tag.node.properties = {}
                        if (!n.tag.node.properties.tagKeys) n.tag.node.properties.tagKeys = {}
                        n.tag.node.properties.tagKeys[tagGroup._key] = value
                        this.graphProcessingService.createChangesEvent([n], {_key: n.tag.node._key, properties: {tagKeys:n.tag.node.properties.tagKeys}}, true)
                    })
                    this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, undefined)
                }
                break
            case FUNCTION.REMOVE_TAGS:
                const selectionToRemove = graphComponent?.selection.filter(n => n.tag?.node?.superType === NODE_SUPERTYPE.ACTION)
                if (selectionToRemove && tagGroup) {
                    selectionToRemove.forEach(n => {
                        if (!n.tag.node.properties) n.tag.node.properties = {}
                        if (!n.tag.node.properties.tagKeys) n.tag.node.properties.tagKeys = {}
                        n.tag.node.properties.tagKeys[tagGroup._key] = undefined
                        const change = {tagKeys:{}}
                        change.tagKeys[tagGroup._key] = null

                        this.graphProcessingService.createChangesEvent([n], {_key: n.tag.node._key, properties:change}, true)
                    })
                    this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, undefined)
                }
                break
            case FUNCTION.DELETE_MODEL:
                this.confirmDeleteModel(linqset)
                break

            case FUNCTION.DELETE_PROJECT:
                this.confirmDeleteProject(project)
                break
            case FUNCTION.EDIT_MODEL_DETAILS:
                this.modelService.openModelDialog(linqset.model, project).subscribe(async result => {
                    if (!result) return
                    try {
                        await this.modelService.saveModel(result, project?._key||CURRENT_STATE_PROJECT_KEY, false)
                        if (result.file) await this.loadLinqsets()
                    }catch(err) {
                        // the error has been shown, and the model is as it was
                    }
                })
                break
            case FUNCTION.EDIT_PROJECT:
                if (project) {
                    this.modelService.openProjectDialog(organisation, project).subscribe(result => {
                        // a failure has been shown, and the project is as it was
                        if (result) this.modelService.saveProject(result).catch(() => {})
                    })
                }
                break
            case FUNCTION.REPLACE_CURRENT_STATE:
                await this.confirmMergeDialog(organisation, project, linqset.model)

                break
            case FUNCTION.REMOVE_GROUPS:
                if (gcComponent && !linqset.isDirty()) {
                    gcComponent.newLinqset()
                }
                break
            case FUNCTION.CREATE_GROUPS:
                if (!linqset.isDirty()) {
                    if (gcComponent) gcComponent.clearingGraph = true
                    try {
                        this.filterService.clearFilter(graphComponent)
                        this.graphService.selectModelItems(graphComponent, [])
                        await this.groupService.createGroups(value, graphComponent.graph, linqset, tagGroup)
                        this.graphService.runLayoutForLinqset(graphComponent, linqset)
                    }finally {
                        if (gcComponent) gcComponent.clearingGraph = false
                    }
                }
                break
            case FUNCTION.TOGGLE_VIEW_DIRECTION:
                // the canvases lay themselves out again in the new direction once it's saved
                if (user && user.preferences.graphDirection !== value) {
                    user.preferences.graphDirection = value
                    await this.userService.saveUserPreferences(user)
                }

                break
            case FUNCTION.APPLY_FILTER:
                this.openFilterDialog(graphComponent, linqset)
                break
            case FUNCTION.CLEAR_FILTER:
                this.filterService.clearFilter(graphComponent)
                break
            case FUNCTION.SAVE_MODEL: {
                // an action or information left with no name and nothing joined to it would show in the analysis as
                // work of its own that leads nowhere: the user can name, join or delete it, or save it as it is
                const empty = emptyNodes(linqset.modelKey, linqset.nodes, linqset.edges)
                if (empty.length && graphComponent && !(await this.confirmSaveWithEmptyNodes(empty))) {
                    const items = graphComponent.graph.nodes.filter(n => empty.includes(n.tag?.node)).toArray()
                    this.graphService.selectModelItems(graphComponent, items)
                    if (items.length) await this.graphService.zoomToSetOfModelNodes(graphComponent, items)
                    break
                }
                await this.saveModel(linqset)
                break
            }
            case FUNCTION.CANCEL_CHANGES:
                await this.loadLinqsets(false, true)
                break
            case FUNCTION.RECALCULATE_INSIGHTS:
                this.graphProcessingService.processGraph(organisation, graphComponent.graph, linqset, this.linqsetRepository.organisationLinqset, false)
                break
            case FUNCTION.RESET_LAYOUT:
                await this.graphService.runLayoutForLinqset(graphComponent, linqset)
                break
            case FUNCTION.CHANGE_LAYOUT:
                // a tag path layout comes with the tag to line the model up by
                if (value && typeof value === 'object') {
                    linqset.layout = value.layout
                    linqset.layoutTag = { groupKey: value.groupKey, tagKey: value.tagKey }
                }else {
                    linqset.layout = value
                    linqset.layoutTag = undefined
                }
                this.graphService.chosenLayouts.add(linqset)
                await this.graphService.runLayoutForLinqset(graphComponent, linqset)
                // the people and systems views open in the layout last chosen for them
                if (user && PEOPLE_SYSTEMS_VIEWS.includes(linqset.modelType) && PEOPLE_SYSTEMS_LAYOUTS.includes(linqset.layout) && user.preferences.peopleSystemsLayout !== linqset.layout) {
                    user.preferences = { ...user.preferences, peopleSystemsLayout: linqset.layout }
                    await this.userService.saveUserPreferences(user, true)
                }
                break
            case FUNCTION.COLLAPSE_ALL:
                gcComponent?.toggleGroups(false)
                break
            case FUNCTION.EXPAND_ALL:
                gcComponent?.toggleGroups(true)
                break
            case FUNCTION.SWITCH_TO_PAN_MODE:
                this.graphService.switchToPanMode(graphComponent)
                break
            case FUNCTION.SWITCH_TO_SELECT_MODE:
                this.graphService.switchToSelectMode(graphComponent)
                break
            case FUNCTION.TOGGLE_MAGNIFYING_GLASS:
                if (gcComponent) gcComponent.toggleMagnifyingGlass()
                break
            case FUNCTION.TOGGLE_HEATMAP:
                const r = this.graphService.toggleHeatmap(graphComponent, linqset)
                if (r) this.snackBar.open("Showing heatmap for " + this.labelService.nodeInsightsField, "close")
                break
            case FUNCTION.ZOOM_IN:
                graphComponent?.executeCommand(Command.INCREASE_ZOOM)
                break
            case FUNCTION.ZOOM_OUT:
                graphComponent?.executeCommand(Command.DECREASE_ZOOM)
                break
            case FUNCTION.RETURN_TO_ORIGINAL:
                graphComponent?.executeCommand(Command.ZOOM)
                break
            case FUNCTION.FIT_TO_SCREEN:
                graphComponent?.executeCommand(Command.FIT_CONTENT)
                break
            case FUNCTION.CUT:
                graphComponent?.executeCommand(Command.CUT)
                break
            case FUNCTION.COPY:
                graphComponent?.executeCommand(Command.COPY)
                break
            case FUNCTION.PASTE:
                graphComponent?.executeCommand(Command.PASTE)
                break
            case FUNCTION.UNDO:
                graphComponent?.executeCommand(Command.UNDO)
                break
            case FUNCTION.REDO:
                graphComponent?.executeCommand(Command.REDO)
                break
            case FUNCTION.GROUP_SELECTION:
                graphComponent?.executeCommand(Command.GROUP_SELECTION)
                break
            case FUNCTION.UNGROUP_SELECTION:
                graphComponent?.executeCommand(Command.UNGROUP_SELECTION)
                break
            case FUNCTION.PAY_SUBSCRIPTION:
                const orgToUse = value as Organisation || organisation
                this.confirmExtensionRequestDialog(orgToUse)
                break
            case FUNCTION.LOGOUT:
                this.linqsetRepository.refreshInsights(linqset)

                this.userService.logout();
                this.organisationService.clear()
                this.linqsetRepository.clear()

                this.router.navigateByUrl('/auth');
                break
            case FUNCTION.BACKUP_MODEL:
                this.backupService.createBackup(organisation, linqset.model, project)
                break
            case FUNCTION.RESTORE_BACKUP:
                await this.backupService.confirmRestoreBackup(organisation, linqset.model, project, value)
                break
            case FUNCTION.RESTORE_MODEL:
                this.openRestoreDialog(organisation)
                break
            case FUNCTION.MERGE_NODES:
                await this.linqsetRepository.mergeNodes(value.toNode._key, value.fromNodeKeys)
                this.loadLinqsets()
                break
            case FUNCTION.CREATE_TICKET:
                await this.createTicket()
                break
            case FUNCTION.MOVE_SELECTION_TO_MODEL:
                this.moveSelectedNodes(gcComponent, linqset)
                break
            case FUNCTION.MAINTAIN_EMISSION_FACTOR:
                this.maintainEmissionsFactor(organisation, value)
                break
            case FUNCTION.DELETE_EMISSION_FACTOR:
                this.deleteEmissionsFactor(organisation, value)
                break
            case FUNCTION.SHOW_HELP_GUIDE:
                this.router.navigateByUrl(helpUrlFrom(this.router.url, value))
                break
            case FUNCTION.REFRESH_INSIGHTS:
                this.refreshInsights()
                break
            case FUNCTION.CLOSE_ORGANISATION:
                this.organisationService.closeOrganisation(organisation)
                break
            case FUNCTION.INCREMENTAL_LAYOUT:
                this.graphService.runLayoutForLinqset(graphComponent, linqset, true)
                break
        default:
            console.log("Function not handled: " + fn.toString())
        }
    }

    private async editOrganisation(organisation:Organisation) {
        const dialogRef2 = this.organisationService.openOrganisationDialog(organisation)

        dialogRef2.afterClosed().subscribe(async result => {
            if (result) {
                await this.organisationService.saveOrganisation(result, false)
            }
        })
    }

    private async loadLinqsets(runInsights?:boolean, loadOrganisation?:boolean) {
        this.linqsetLoadedEvent.emit(loadOrganisation?"loadOrganisation":(runInsights?"runInsights":""))
    }

    private async saveModel(linqset:Linqset) {
        try {
            const reload = linqset.reloadOnSave         // need to do this before it is changed on save
            await this.linqsetRepository.saveLinqset(linqset)
            if (reload) this.loadLinqsets(true, true)

        }catch(err) {
            this.handleSaveError(linqset, err)
        }
    }

    // true to save with the empty nodes as they are; false (or closing the dialog) to go back and show them
    private async confirmSaveWithEmptyNodes(nodes:LinqsetNode[]):Promise<boolean> {
        const kinds = (superType:NODE_SUPERTYPE, one:string, many:string) => {
            const n = nodes.filter(node => node.superType === superType).length
            return n ? `${n} ${n === 1 ? one : many}` : ''
        }
        const what = [kinds(NODE_SUPERTYPE.ACTION, 'action', 'actions'), kinds(NODE_SUPERTYPE.INFORMATION, 'information node', 'information nodes')].filter(k => k).join(' and ')
        const dialogData = new ConfirmDialogModel("Unnamed, unconnected nodes", [
            `${what} ${nodes.length === 1 ? 'has' : 'have'} no name and nothing connected to ${nodes.length === 1 ? 'it' : 'them'}. Saved like this, ${nodes.length === 1 ? 'it shows' : 'they show'} in the analysis as work that leads nowhere.`,
            `Show ${nodes.length === 1 ? 'it' : 'them'} to name, connect or delete, or save the model as it is.`,
        ], undefined, false, "Save anyway", "Show " + (nodes.length === 1 ? "it" : "them"))
        const result = await firstValueFrom(this.dialog.open(ConfirmDialogComponent, { maxWidth: "460px", data: dialogData }).afterClosed())
        return result?.button === "Save anyway"
    }

    // a failed save leaves its changes on the model, so trying again saves them
    private handleSaveError(linqsetToSave:Linqset, err:any) {
        let message = getUserMessage(err);
        // a node still used elsewhere would only be refused again, so the changes are kept to undo the delete, or to
        // remove the node where it's used first
        if (err?.msgCode === ERROR_CODE.ERR235) {
            const inUse = new ConfirmDialogModel("Still in use", [message], undefined, false, "Cancel and refresh", "Keep editing")
            this.dialog.open(ConfirmDialogComponent, {maxWidth: "460px", data: inUse}).afterClosed().subscribe(result => {
                if (result?.button === "Cancel and refresh") this.loadLinqsets()
            })
            return
        }
        const dialogData = new ConfirmDialogModel("Confirm Action", [message], undefined, false, "Try again", "Cancel and refresh");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "400px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                if (dialogResult.button === "Try again") {
                    this.saveModel(linqsetToSave)
                }else {
                    this.loadLinqsets()
                }
            }else this.loadLinqsets()
        })
    }

    private async createProject(organisation:Organisation) {
        this.modelService.openProjectDialog(organisation).subscribe(async result => {
            if (result)  {
                try {
                    // the server works out the new project's insights as it creates it
                    await this.modelService.saveProject(result)
                    await this.router.navigateByUrl(PROJECTS_URL(organisation._key, result._key))
                }catch(err) {
                    this.snackBar.open(getUserMessage(err), "close")
                }
            }
        })
    }

    private async refreshInsights(linqset?:Linqset) {
        // a failure is reported by the repository
        if (await this.linqsetRepository.refreshInsights(linqset)) {
            this.snackBar.open("Organisation insights refreshed", "close", {duration:5000})
        }
    }

    // the loaded organisation model (current state or the project being viewed); emission factor usage is worked out from it
    get emissionUsageLinqset() {
        return this.linqsetRepository.organisationLinqset
    }

    // infrastructure in the loaded organisation model that uses the factor, for a quick check; the server checks every workspace
    emissionFactorUsage(factorKey:string) {
        return (this.linqsetRepository.organisationLinqset?.nodes || [])
            .filter(n => n.superType === NODE_SUPERTYPE.INFRASTRUCTURE && n.properties?.emissions?.some(e => e.emissionFactorKey === factorKey))
    }

    private async deleteEmissionsFactor(organisation:Organisation, factor?:EmissionFactor) {
        if (!factor) return
        if (!this.linqsetRepository.organisationLinqset) {
            await this.linqsetRepository.loadModelData(organisation._key, ORGANISATION_MODEL_KEY, CURRENT_STATE_PROJECT_KEY, false)
        }
        if (!this.linqsetRepository.organisationLinqset) {
            this.snackBar.open("Couldn't check whether the emission factor is in use, so it hasn't been deleted. Try again.", "close")
            return
        }

        const usage = this.emissionFactorUsage(factor._key)
        if (usage.length > 0) {
            this.snackBar.open(`${factor.name} is used by ${usage.map(n => n.name||"Infrastructure").join(', ')}, so it can't be deleted. Remove it from those in the people and systems model first.`, "close")
            return
        }

        const dialogData = new ConfirmDialogModel("Delete emission factor", [`Delete the emission factor ${factor.name}? It isn't used by any infrastructure.`], undefined, false, "Delete", "Cancel");
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "400px",
            data: dialogData
        });

        const dialogResult = await lastValueFrom(dialogRef.afterClosed())
        if (dialogResult?.button === "Delete") {
            // the server also checks every workspace, and explains if the factor is used elsewhere
            if (await this.organisationService.saveEmissionFactorList(organisation, organisation.emissionFactors.filter(ef => ef._key !== factor._key)))
                this.snackBar.open("Emission factor deleted", "close", {duration:5000})
        }
    }

    private maintainEmissionsFactor(organisation:Organisation, factor?:EmissionFactor) {
        const data = factor || {_key:v4(), name:'', description:'', value:0} as EmissionFactor

        const dialogRef = this.dialog.open(EmissionFactorDialogComponent, {data, hasBackdrop:true, width:"800px"});

        dialogRef.afterClosed().subscribe(async (result:EmissionFactor) => {
            if (!result) return
            const factors = organisation.emissionFactors || []
            // an edited factor keeps its place in the list
            const saved = factors.some(ef => ef._key === result._key)
                ? factors.map(ef => ef._key === result._key ? result : ef)
                : [...factors, result]
            if (await this.organisationService.saveEmissionFactorList(organisation, saved))
                this.snackBar.open("Emission factor saved", "close", {duration:5000})
        });
    }

    // greys out the roles and systems behind a node, and theirs in turn; ones already done are skipped, so a loop ends
    private excludeAttachedRoleSystemNode(node:INode, graph:IGraph, linqset:Linqset, done = new Set<INode>()) {
        graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => e.sourceNode.tag?.node && isRoleSystem(e.sourceNode.tag.node.superType) && !done.has(e.sourceNode)).forEach(rse => {
            done.add(rse.sourceNode)
            if (rse.tag) rse.tag.excluded = true
            rse.sourceNode.tag.excludeVisualisation()
            this.nodeService.setNodeStyle(graph, rse.sourceNode, linqset)
            this.excludeAttachedRoleSystemNode(rse.sourceNode, graph, linqset, done)
        })
    }

    private moveSelectedNodes(gcComponent:GraphComponentHost, linqset:Linqset) {
        if (!gcComponent) return
        const selectedActions = gcComponent.getSelectedNodes().filter(n => n.tag?.node?.superType === NODE_SUPERTYPE.ACTION)
        if (selectedActions.length > 0) {
            const newModelKey = v4()

            try {

                this.modelService.openModelDialog(null, linqset.project, true).subscribe(async result => {
                    if (result)  {
                        result.model._key = newModelKey
                        linqset.changes.newModel = result.model
                        // the new model's own insights, not the model the nodes are moving from
                        linqset.changes.newModel.insights = [createModelInsights(linqset.organisationKey, linqset.workspaceKey, newModelKey)]

                        const {nodes, edges} = this.graphService.moveSelection(gcComponent.graphComponent, selectedActions, linqset.modelKey, newModelKey)

                        if (nodes.length === 0) {
                            this.snackBar.open("No compatible selection to move. Select one or more action nodes.", "close")
                        }else {
                            const movedNodes = nodes.filter(n => n.tag.node.modelKey === newModelKey)
                            const movedEdges = edges.filter(e => e.tag.edge.modelKey===newModelKey)
                            const externalNodes = nodes.filter(n => n.tag.node.external)

                            this.graphProcessingService.createChangesEvent(externalNodes, {external:true}, true)
                            this.graphProcessingService.createChangesEvent(movedNodes, {modelKey:newModelKey}, true)
                            this.graphProcessingService.createEdgeChangesEvent(edges.filter(e => (e.tag.edge.type === EDGE_TYPE.JOINS  || e.tag.edge.type === EDGE_TYPE.USED_IN)), true)

                            movedNodes.forEach(n => {
                                n.tag.excludeNode()
                                this.nodeService.setNodeStyle(gcComponent.graphComponent.graph, n, linqset)
                                this.excludeAttachedRoleSystemNode(n, gcComponent.graphComponent.graph, linqset)
                            })

                            movedEdges.forEach(e => {
                                e.tag.excluded = true
                            })
                            // now process graph
                            this.graphProcessingService.createEdgeChangesEvent(movedEdges, false)

                            this.snackBar.open("The nodes to be moved are greyed out. If this looks correct, save the model and the nodes will be moved.", "close")
                        }
                    }
                })
            }catch(err) {
                this.snackBar.open(getUserMessage(err), "close")
            }
        }else {
            this.snackBar.open("You must select at least 1 action node to move.", "close")
        }
    }

    // a new model can be built by AI, with getting started, or started blank. Resolves to the choice, or nothing if cancelled
    async askHowToStartModel():Promise<'ai'|'blank'|undefined> {
        const dialogData = new ConfirmDialogModel("Create model", [
            "Would you like AI to build it? Tell AI about the process and it creates the steps, people and systems, for you to check and change.",
            "Or start with a blank model and build it yourself.",
        ], undefined, false, NEW_MODEL_AI, "Cancel", NEW_MODEL_BLANK)
        const result = await lastValueFrom(this.dialog.open(ConfirmDialogComponent, {maxWidth: "500px", data: dialogData}).afterClosed())
        if (result?.button === NEW_MODEL_AI) return 'ai'
        if (result?.button === NEW_MODEL_BLANK) return 'blank'
        return undefined
    }

    private async createModel(organisation:Organisation, project:Project) {
        const start = await this.askHowToStartModel()
        if (start === 'ai') {
            this.router.navigateByUrl(CREATE_MODEL_WITH_AI_URL(organisation._key, project?._key))
            return
        }
        if (start !== 'blank') return

        this.modelService.openModelDialog(null, project).subscribe(async result => {
            if (!result) return
            const workspaceKey = project?._key || CURRENT_STATE_PROJECT_KEY
            try {
                const model = await this.modelService.saveModel(result, workspaceKey, true)
                this.router.navigateByUrl(SKETCH_URL(organisation._key, model._key, MODEL_TYPE.PROCESS, workspaceKey))
            }catch(err) {
                // the error has been shown; there is no model to open
            }
        })
    }

    public async createTicket() {
        const data = {subject:'', description:''} as SupportTicket

        const dialogRef = this.dialog.open(TicketDialogComponent, {data, hasBackdrop:true, width:'600px', maxWidth:'95vw'});

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                try {
                    await this.userService.createSupportTicket(dialogResult)
                    clearTicketDraft()
                    this.reloadTicketsEvent.emit('')
                }catch(err) {
                    // the error has already been shown; the ticket is kept as a draft to send again
                }
            }
        });
    }

    public async confirmExtensionRequestDialog(organisation:Organisation) {
        let message = `Request an extension for ${organisation.name}?`;
        const dialogData = new ConfirmDialogModel("Request extension", [message], undefined, true, "Make request", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                await this.organisationService.requestExtension(organisation, dialogResult.notes)
            }
        });
    }

    public async confirmMergeDialog(organisation:Organisation, project:Project, model:ModelDetails) {
        let message = `This will replace the current state graph with this one. It cannot be undone. Are you sure?`;
        const dialogData = new ConfirmDialogModel("Confirm Action", [message], "replace");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                await this.linqsetRepository.mergeProjectWithCurrentState(organisation._key, project._key, model._key)
                this.router.navigateByUrl(PROJECTS_URL(organisation._key, project._key))
            }
        });
    }

    public async confirmDeleteProject(project:Project) {
        const message = `This will permanently delete the project ${project.name} and all of its models. This can't be undone.`;
        const dialogData = new ConfirmDialogModel("Delete project", [message], "delete");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                await this.organisationService.deleteProject(project)
                // leave the project if it was the one being viewed
                const organisationKey = this.organisationService.organisation._key
                if (this.router.url.startsWith(PROJECTS_URL(organisationKey, project._key))) await this.router.navigateByUrl(DEFAULT_URL(organisationKey))
                else this.loadLinqsets()
            }
        });
    }

    public async confirmDeleteModel(linqset:Linqset) {
        const message = linqset.isProjectModel()
            ? `Permanently delete the model ${linqset.model.name} from this project? This can't be undone.`
            : `Archive the model ${linqset.model.name}? Archived models are hidden and can be restored from the menu for ${MODEL_EXPIRY_DAYS} days, after which they are deleted. Delete permanently removes it now.`;

        /*if (!linqset.isCurrentState()) {
            const projects = organisation.models.filter(s => s.currentStateKey === linqset.modelKey)
            if (projects.length > 0) {
                message = `This model has ${projects.length===1?'a project model associated with it. Deleting the workspace will also delete this project model.':projects.length + ' project models associated with it. Deleting the workspace will also delete these project models.'} This cannot be undone. Are you sure?`
            }
        }*/

        const dialogData = linqset.isProjectModel()?new ConfirmDialogModel("Delete model", [message], "delete", false, "Delete permanently", "Cancel"):new ConfirmDialogModel("Archive model", [message], "delete", false, "Archive", "Cancel", "Delete permanently");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });

        const dialogResult = await lastValueFrom(dialogRef.afterClosed())
        if (dialogResult) {
            await this.organisationService.deleteModel(linqset.model, linqset.workspaceKey, dialogResult.button==="Delete permanently")
            this.linqsetRepository.refreshInsights(linqset).then(() =>{
                this.loadLinqsets()
            })
        }
    }

    private openFilterDialog(graphComponent:GraphComponent, linqset:Linqset) {
        const data = {graphComponent, linqset}

        const dialogRef = this.dialog.open(FilterComponent, {data, width:'800px', hasBackdrop:true});
    }

    private openRestoreDialog(organisation:Organisation) {
        const data = organisation
        const dialogRef = this.dialog.open(RestoreModelDialogComponent, {data, width:'1000px', hasBackdrop:true});

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                try {
                    if (dialogResult.action === "archive")
                        await this.organisationService.restoreModels(dialogResult.models)
                    else if (dialogResult.action === "delete")
                        await this.organisationService.deleteModelKeys(dialogResult.models)
                }catch(err) {
                    // shown already; the ones done before it still count
                }finally {
                    this.loadLinqsets()
                }
            }
        });

    }
}